import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { useSubmitQuoteItemEdits } from '~/api';
import { Modal } from '~/components/overlays/Modal';
import { ModalHeader } from '~/components/overlays/ModalHeader';
import { Button } from '~/components/ui';
import { QuoteEditForm } from '~/pages/rfq-view/QuoteCard';
import { CounterQuoteForm } from './CounterQuoteAction';
import type { CounterSubmission } from './useSendCounter';
import { sellerLabel, toastGraphQLError } from '~/utils';
import type { QuoteNegotiationContext } from './quoteNegotiation';
import type { Quote } from './splitAwardGridTypes';

type RespondForSellerActionProps = Readonly<{
  quote: Quote;
  negotiation: QuoteNegotiationContext;
  answersCounter?: boolean;
}>;

export function RespondForSellerAction({
  quote,
  negotiation,
  answersCounter = false,
}: RespondForSellerActionProps) {
  const { t } = useTranslation('rfq-view');
  const [isOpen, setIsOpen] = useState(false);

  const sellerName = sellerLabel(quote.sellingOrganization);
  const close = () => setIsOpen(false);
  const { mutateAsync: submitEdits } = useSubmitQuoteItemEdits();

  const answerCounterForSeller = async (submission: CounterSubmission) => {
    try {
      await submitEdits({
        quoteId: quote.id,
        input: {
          ...submission,
          sellingOrganizationFacilityId: null,
          submit: true,
        },
      });
      toast.success(t('rfq_view.seller_response_recorded'));
      close();
    } catch (error) {
      toastGraphQLError(error);
    }
  };

  return (
    <>
      <Button
        variant="secondary"
        size="sm"
        width="auto"
        data-testid={`respond-for-seller-${quote.id}`}
        aria-label={t('rfq_view.respond_for_seller_aria_label', {
          sellerName,
        })}
        onClick={() => setIsOpen(true)}
      >
        {t('rfq_view.respond_for_seller_button')}
      </Button>
      {isOpen && (
        <Modal isOpen onClose={close}>
          <div
            className="bg-neut-bg-alt rounded-lg w-full max-w-[60rem]"
            data-testid={`respond-for-seller-modal-${quote.id}`}
          >
            <ModalHeader
              title={t('rfq_view.respond_for_seller_aria_label', {
                sellerName,
              })}
              onClose={close}
            />
            <div className="p-6 max-h-[70vh] overflow-y-auto">
              {answersCounter ? (
                <CounterQuoteForm
                  quote={quote}
                  groups={negotiation.groups}
                  usesWholeRequestPrice={negotiation.usesWholeRequestPrice}
                  requestForQuoteCurrency={negotiation.requestForQuoteCurrency}
                  submitCounter={answerCounterForSeller}
                />
              ) : (
                <QuoteEditForm
                  quote={quote}
                  groups={negotiation.groups}
                  requestForQuoteUsesTallyPrice={
                    negotiation.usesWholeRequestPrice
                  }
                  requestForQuoteHasCannedTally={negotiation.hasCannedTally}
                  requestForQuoteCurrency={negotiation.requestForQuoteCurrency}
                  linkedOrganizationId={quote.sellingOrganization.id}
                  onSubmitted={close}
                />
              )}
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}
